/*============================================================
   REACT DEPENDENCIES
============================================================*/
import { useState, useEffect } from "react";

/*============================================================
   REDUX DEPENDENCIES
============================================================*/
import { useDispatch, useSelector } from "react-redux";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid2";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import TextareaAutosize from "@mui/material/TextareaAutosize";

import GridSectionHeader from "../../../widgets/GridSectionHeader";
import GridTextfield from "../../../widgets/GridTextfield";

import {
  valudateTextfield2,
  validateCurrencyField3,
  removeCommaFromCurrency,
  convertLocaleString,
} from "../../../../utils/fieldValidator.js";

//DATA SLICE - REDUCER
import { updateIncomeExpense, updateCPFIncomeExpense, updateImpact } from "../../../../states/slcIncomeExpense.js";

import PageNavigator, { onNext } from "../../../PageNavigator";

// INFLOW & OUTFLOW
var monthlyIncomeError = "";
var monthlyExpensesError = "";
var noIncomeExpensesReasonError = "";

// CPF - INFLOW & OUTFLOW
var ordinaryAcctError = "";
var specialAcctError = "";
var medisaveAcctError = "";
var noCpfInFlowReasonError = "";

var ordinaryAcctMortgageError = "";
var ordinaryAcctInvestmentError = "";
var specialAcctInvestmentError = "";
var medisaveAcctOutflowError = "";
var noCpfOutFlowReasonError = "";

var haveInExImpactError = "";
var cpfInExFlowImpactDescError = "";

export default function IncomeAndExpenses() {
  const income = useSelector((state) => state.incomeexpense);

  //FORM VALIDATATION STATUS
  const [isValid, setIsValid] = useState(false);

  //INFLOW & OUTFLOW
  const [monthlyIncome, setMonthlyIncome] = useState(income.monthlyIncome);
  const [monthlyExpenses, setMonthlyExpenses] = useState(income.monthlyExpenses);
  const [noIncomeExpensesReason, setNoIncomeExpensesReason] = useState(income.noIncomeExpensesReason);

  //CPF INFLOW & OUTFLOW
  const [ordinaryAcct, setOrdinaryAccount] = useState(income.ordinaryaccount);
  const [specialAcct, setSpecialAccount] = useState(income.specialaccount);
  const [medisaveAcct, setMedisaveAccount] = useState(income.medisaveaccount);
  const [noCpfInFlowReason, setNoCpfInFlowReason] = useState(income.noCpfInFlowReason);
  const [cpfInflowTotal, setCpfInflowTotal] = useState(income.cpfInflowTotal);

  const [ordinaryAcctMortgage, setOrdinaryAcctMortgage] = useState(income.ordinaryAcctMortgage);
  const [ordinaryAcctInvestment, setOrdinaryAcctInvestment] = useState(income.ordinaryAcctInvestment);
  const [specialAcctInvestment, setSpecialAcctInvestment] = useState(income.specialAcctInvestment);
  const [medisaveAcctOutflow, setMedisaveAcctOutflow] = useState(income.medisaveAcctOutflow);
  const [noCpfOutFlowReason, setNoCpfOutFlowReason] = useState(income.noCpfOutFlowReason);
  const [cpfOutlowTotal, setCpfOutlowTotal] = useState(income.cpfOutFlowTotal);

  const [haveInExImpact, setHaveInExImpact] = useState(income.haveInExImpact);
  const [cpfInExFlowImpactDesc, setCpfInExFlowImpactDesc] = useState(income.cpfInExFlowImpactDesc);

  const dispatch = useDispatch();

  //COMPONENT ID
  let id = "";

  useEffect(() => {
    let inTotal =
      Number(removeCommaFromCurrency(ordinaryAcct)) +
      Number(removeCommaFromCurrency(specialAcct)) +
      Number(removeCommaFromCurrency(medisaveAcct));

    inTotal = convertLocaleString(inTotal, "en-US", 2);

    updateRedux("cpfInflowTotal", inTotal, updateCPFIncomeExpense);
    setCpfInflowTotal(inTotal);
  }, [ordinaryAcct, specialAcct, medisaveAcct]);

  useEffect(() => {
    let outTotal =
      Number(removeCommaFromCurrency(ordinaryAcctMortgage)) +
      Number(removeCommaFromCurrency(ordinaryAcctInvestment)) +
      Number(removeCommaFromCurrency(specialAcctInvestment)) +
      Number(removeCommaFromCurrency(medisaveAcctOutflow));

    outTotal = convertLocaleString(outTotal, "en-US", 2);

    updateRedux("cpfOutlowTotal", outTotal, updateCPFIncomeExpense);
    setCpfOutlowTotal(outTotal);
  }, [ordinaryAcctMortgage, ordinaryAcctInvestment, specialAcctInvestment, medisaveAcctOutflow, updateRedux]);

  function validateForm(e) {
    e.preventDefault();

    /*
      1. ENSURES POSSIBLE FACTOR IMPACTING INCOME / EXPENSE IS INDICATED
      2. ENSURES REASON IS PROVIDED, IF THERE'S A FACTOR
    */
    if (haveInExImpact === null || haveInExImpact.length === 0) {
      haveInExImpactError = "Please select an answer.";
      setHaveInExImpact(null);
      setIsValid(false);
      return;
    }

    if (cpfInExFlowImpactDesc === null || (haveInExImpact === "YES" && cpfInExFlowImpactDesc.length === 0)) {
      cpfInExFlowImpactDescError = "Please provide a reason";
      setCpfInExFlowImpactDesc(null);
      setIsValid(false);
      return;
    }

    setIsValid(true);
    onNext();
  }

  /**
   * 1. VALIDITE FORM USING ERROR MESSAGES
   *     "CALCULATE VALIDITY" IS SIMILAR TO "VALIDATE FORM" IT VALIDATES BY ERROR MESSAGE.
   *       WHEN ALL FIELD'S ERROR MESSAGES ARE EMPTY. (VALID = TRUE)
   *
   * 2. THIS FUNCTION IS ALSO RESPONSIBLE FOR ENABLING / DISABLING THE NEXT BUYTTON, THEREFORE IS TRIGGERED BY EACH UI EVENT
   */
  function calculateValidity() {
    // INFLOW & OUTFLOW
    if (haveInExImpactError === "" && cpfInExFlowImpactDescError === "") setIsValid(true);
    else setIsValid(false);
  }

  function updateRedux(fieldName, fieldValue, reducer) {
    const action = {
      type: fieldName,
      payload: fieldValue,
    };
    dispatch(reducer(action));
  }

  function handleChange(e, object) {
    id = "";

    if (e instanceof PointerEvent) {
      //DROPDOWNS
      id = object.props.id.toLowerCase();
    } else {
      //RADIO BUTTONS & TEXTFIELDS
      let type = "";
      let name = "";

      let input_data = {
        event: e,
        fieldName: "",
        fieldVariable: "",
        isMandatory: false,
      };

      id = e.target.id;

      if (id === "") {
        type = e.target.type;
        name = e.target.name;
      }

      /*====================
      INFLOW & OUTFLOW
      ====================*/
      if (id === "monthlyIncome") {
        input_data.fieldName = "Monthly income";
        input_data.fieldVariable = monthlyIncome;
        const result = validateCurrencyField3(input_data);

        if (result.errorMessage === "") {
          //Annual Amount - Calculation
          let annualAmount = Number(removeCommaFromCurrency(result.value)) * 12;
          annualAmount = convertLocaleString(annualAmount, "en-US", 2);

          setMonthlyIncome(result.value);
          updateRedux("monthlyIncome", result.value, updateIncomeExpense);
          updateRedux("annualIncome", annualAmount, updateIncomeExpense);
        } else monthlyIncomeError = result.errorMessage;
      }

      if (id === "monthlyExpenses") {
        input_data.fieldName = "Monthly expenses";
        input_data.fieldVariable = monthlyExpenses;
        const result = validateCurrencyField3(input_data);

        if (result.errorMessage === "") {
          let annualAmount = Number(removeCommaFromCurrency(result.value)) * 12;
          annualAmount = convertLocaleString(annualAmount, "en-US", 2);

          setMonthlyExpenses(result.value);
          updateRedux("monthlyExpenses", result.value, updateIncomeExpense);
          updateRedux("annualExpenses", annualAmount, updateIncomeExpense);
        } else monthlyExpensesError = result.errorMessage;
      }

      if (id === "noIncomeExpensesReason") {
        input_data.fieldName = "Reason";
        input_data.fieldVariable = noIncomeExpensesReason;
        const result = valudateTextfield2(input_data);

        if (result.errorMessage === "") {
          setNoIncomeExpensesReason(result.value);
          updateRedux("noIncomeExpensesReason", result.value, updateIncomeExpense);
        } else noIncomeExpensesReasonError = result.errorMessage;
      }

      /*====================
      CPF - INFLOW
      ====================*/
      if (id === "ordinaryAcct") {
        input_data.fieldName = "Orginaly account's income";
        input_data.fieldVariable = ordinaryAcct;
        const result = validateCurrencyField3(input_data);

        if (result.errorMessage === "") {
          setOrdinaryAccount(result.value);
          updateRedux("ordinaryaccount", result.value, updateCPFIncomeExpense);
        } else ordinaryAcctError = result.errorMessage;
      }

      if (id === "specialAcct") {
        input_data.fieldName = "Special account's income";
        input_data.fieldVariable = specialAcct;
        const result = validateCurrencyField3(input_data);

        if (result.errorMessage === "") {
          setSpecialAccount(result.value);
          updateRedux("specialaccount", result.value, updateCPFIncomeExpense);
        } else specialAcctError = result.errorMessage;
      }

      if (id === "medisaveAcct") {
        input_data.fieldName = "Medisave account's income";
        input_data.fieldVariable = medisaveAcct;
        const result = validateCurrencyField3(input_data);

        if (result.errorMessage === "") {
          setMedisaveAccount(result.value);
          updateRedux("medisaveAcct", result.value, updateCPFIncomeExpense);
        } else medisaveAcctError = result.errorMessage;
      }

      if (id === "noCpfInFlowReason") {
        input_data.fieldName = "Reason";
        input_data.fieldVariable = noCpfInFlowReason;
        const result = valudateTextfield2(input_data);

        if (result.errorMessage === "") {
          setNoCpfInFlowReason(result.value);
          updateRedux("noCpfInFlowReason", result.value, updateCPFIncomeExpense);
        } else noCpfInFlowReasonError = result.errorMessage;
      }

      /*====================
      CPF - OUTFLOW
      ====================*/
      if (id === "ordinaryAcctMortgage") {
        // let data = {
        //   event: e,
        //   fieldName: "Ordinary Account Mortage expense",
        //   fieldVariable: ordinaryAcctMortgage,
        //   setter: setOrdinaryAcctMortgage,
        //   isMandatory: false,
        // };
        // ordinaryAcctMortgageError = validateCurrencyField(data);

        input_data.fieldName = "Ordinary Account Mortage expense";
        input_data.fieldVariable = ordinaryAcctMortgage;
        const result = validateCurrencyField3(input_data);

        if (result.errorMessage === "") {
          setOrdinaryAcctMortgage(result.value);
          updateRedux("ordinaryAcctMortgage", result.value, updateCPFIncomeExpense);
        } else ordinaryAcctMortgageError = result.errorMessage;
      }

      if (id === "ordinaryAcctInvestment") {
        // let data = {
        //   event: e,
        //   fieldName: "Ordinary Account Investment expense",
        //   fieldVariable: ordinaryAcctInvestment,
        //   setter: setOrdinaryAcctInvestment,
        //   isMandatory: false,
        // };
        // ordinaryAcctInvestmentError = validateCurrencyField(data);

        input_data.fieldName = "Ordinary Account Investment expense";
        input_data.fieldVariable = ordinaryAcctInvestment;
        const result = validateCurrencyField3(input_data);

        if (result.errorMessage === "") {
          setOrdinaryAcctInvestment(result.value);
          updateRedux("ordinaryAcctInvestment", result.value, updateCPFIncomeExpense);
        } else ordinaryAcctInvestmentError = result.errorMessage;
      }

      if (id === "specialAcctInvestment") {
        // let data = {
        //   event: e,
        //   fieldName: "Special Account Investment expense",
        //   fieldVariable: specialAcctInvestment,
        //   setter: setSpecialAcctInvestment,
        //   isMandatory: false,
        // };
        // specialAcctInvestmentError = validateCurrencyField(data);

        input_data.fieldName = "Special Account Investment expense";
        input_data.fieldVariable = specialAcctInvestment;
        const result = validateCurrencyField3(input_data);

        if (result.errorMessage === "") {
          setSpecialAcctInvestment(result.value);
          updateRedux("specialAcctInvestment", result.value, updateCPFIncomeExpense);
        } else specialAcctInvestmentError = result.errorMessage;
      }

      if (id === "medisaveAcctOutflow") {
        // let data = {
        //   event: e,
        //   fieldName: "Medisave Account expense",
        //   fieldVariable: medisaveAcctOutflow,
        //   setter: setMedisaveAcctOutflow,
        //   isMandatory: false,
        // };
        // medisaveAcctOutflowError = validateCurrencyField(data);

        input_data.fieldName = "Medisave Account expense";
        input_data.fieldVariable = medisaveAcctOutflow;
        const result = validateCurrencyField3(input_data);

        if (result.errorMessage === "") {
          setMedisaveAcctOutflow(result.value);
          updateRedux("medisaveAcctOutflow", result.value, updateCPFIncomeExpense);
        } else medisaveAcctOutflowError = result.errorMessage;
      }

      if (id === "noCpfOutFlowReason") {
        // let data = {
        //   event: e,
        //   fieldName: "Reason",
        //   fieldVariable: noCpfOutFlowReason,
        //   setter: setNoCpfOutFlowReason,
        //   isMandatory: false,
        // };
        // noCpfOutFlowReasonError = valudateTextfield(data);

        input_data.fieldName = "Reason";
        input_data.fieldVariable = noCpfOutFlowReason;
        const result = valudateTextfield2(input_data);

        if (result.errorMessage === "") {
          setNoCpfOutFlowReason(result.value);
          updateRedux("noCpfOutFlowReason", result.value, updateCPFIncomeExpense);
        } else noCpfOutFlowReasonError = result.errorMessage;
      }

      /*====================
      IMPACT FACTORS & REASONS
      ====================*/
      if (type === "radio" && name === "haveInExImpact") {
        setHaveInExImpact(e.target.value.toUpperCase());
        haveInExImpactError = "";

        updateRedux("haveInExImpact", e.target.value.toUpperCase(), updateImpact);
      }

      if (id === "cpfInExFlowImpactDesc") {
        // let data = {
        //   event: e,
        //   fieldName: "Reason",
        //   fieldVariable: cpfInExFlowImpactDesc,
        //   setter: setCpfInExFlowImpactDesc,
        // };
        // cpfInExFlowImpactDescError = valudateTextfield(data);

        input_data.fieldName = "Reason";
        input_data.fieldVariable = cpfInExFlowImpactDesc;
        const result = valudateTextfield2(input_data);

        if (result.errorMessage === "") {
          setCpfInExFlowImpactDesc(result.value);
          updateRedux("cpfInExFlowImpactDesc", result.value, updateImpact);
        } else cpfInExFlowImpactDescError = result.errorMessage;
      }

      calculateValidity();
    }
  }

  return (
    <>
      <Box component="form" onSubmit={validateForm} noValidate>
        {/* INFLOW & OUTFLOW */}
        {/* SIMPLIFIED INCOME & EXPENSES */}
        {/* <SimplifiedInflowAndOutflow /> */}
        <Grid container spacing={2}>
          <GridSectionHeader
            sectionName="Inflow & Outflow"
            sx={{
              p: 1,
              pl: 1.8,
              mt: 0,
              mb: 1,
              fontSize: 18,
              fontWeight: "bold",
              color: "#FFFFFF", //WHITE
              backgroundColor: "#10B981",
            }}
          />

          <GridTextfield
            id="monthlyIncome"
            key="monthlyIncome"
            required={false}
            columnSize="6"
            label="Total Monthly Income"
            currState={monthlyIncome}
            onChange={handleChange}
            error={monthlyIncomeError}
            helperText={monthlyIncomeError}
            isCurrency={true}
          />

          <GridTextfield
            id="monthlyExpenses"
            key="monthlyExpenses"
            required={false}
            columnSize="6"
            label="Total Monthly Expenses"
            currState={monthlyExpenses}
            onChange={handleChange}
            error={monthlyExpensesError}
            helperText={monthlyExpensesError}
            isCurrency={true}
          />

          <Grid size={12}>
            {monthlyIncome === "" && monthlyExpenses === "" ? (
              <Typography variant="body1" gutterBottom>
                Please input justification for not disclosing all inflow & outflow
                <TextareaAutosize
                  id="noIncomeExpensesReason"
                  placeholder="Type here..."
                  style={{ width: "100%" }}
                  minRows={5}
                  onChange={handleChange}
                  defaultValue={noIncomeExpensesReason}
                />
                {noIncomeExpensesReasonError && (
                  <FormHelperText error={noIncomeExpensesReasonError}>{noIncomeExpensesReasonError}</FormHelperText>
                )}
              </Typography>
            ) : (
              ""
            )}
          </Grid>
        </Grid>

        {/* CPF - INCOME & EXPENSES */}
        <Grid container spacing={2}>
          <GridSectionHeader
            sectionName="CPF Inflow & Outflow"
            sx={{
              p: 1,
              pl: 1.8,
              mt: 0,
              mb: 1,
              fontSize: 18,
              fontWeight: "bold",
              color: "#FFFFFF", //WHITE
              // color: "#000000", //BLACK
              // backgroundColor: "#d9d9d9",
              backgroundColor: "#10B981",
            }}
          />
          <Grid size={12}>
            <Typography variant="h6" gutterBottom>
              Please provide <b>monthly</b> CPF inflow & outflow.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ p: 2, my: 1, border: "1px solid", borderRadius: 5 }}>
              CPF Contribution Calculator
              <List disablePadding sx={{ pl: 4, listStyleType: "disc" }}>
                <ListItem disablePadding sx={{ pl: 1, display: "list-item" }}>
                  <Typography variant="body1">
                    If you are employed under an organisation.{" "}
                    <a href="https://www.cpf.gov.sg/member/tools-and-services/calculators/cpf-contribution-calculator">
                      Click Here
                    </a>
                  </Typography>
                </ListItem>

                <ListItem disablePadding sx={{ pl: 1, display: "list-item" }}>
                  <Typography variant="body1">
                    If you are self-employed.{" "}
                    <a href="https://www.cpf.gov.sg/member/tools-and-services/calculators/self-employed-medisave-contribution-calculator">
                      Click Here
                    </a>
                  </Typography>
                </ListItem>
              </List>
            </Typography>
          </Grid>

          {/*==============================
          CPF INFLOW
          ==============================*/}
          <Grid size={12}>
            <Typography variant="h6" gutterBottom sx={{ backgroundColor: "lightgray" }}>
              CPF Inflow <b>Total: ${cpfInflowTotal}</b>
            </Typography>
          </Grid>

          {/* CPF IN - Ordinary Account */}
          <GridTextfield
            id="ordinaryAcct"
            key="ordinaryAcct"
            required={false}
            columnSize="6"
            label="Ordinary Account"
            currState={ordinaryAcct}
            onChange={handleChange}
            error={ordinaryAcctError}
            helperText={ordinaryAcctError}
            isCurrency={true}
          />

          {/* CPF IN - Special Account */}
          <GridTextfield
            id="specialAcct"
            key="specialAcct"
            required={false}
            columnSize="6"
            label="Special Account"
            currState={specialAcct}
            onChange={handleChange}
            error={specialAcctError}
            helperText={specialAcctError}
            isCurrency={true}
          />

          {/* CPF IN - Medisave Account */}
          <GridTextfield
            id="medisaveAcct"
            key="medisaveAcct"
            required={false}
            columnSize="6"
            label="Medisave Account"
            currState={medisaveAcct}
            onChange={handleChange}
            error={medisaveAcctError}
            helperText={medisaveAcctError}
            isCurrency={true}
          />

          <Grid size={12}>
            {ordinaryAcct === "" && specialAcct === "" && medisaveAcct === "" ? (
              <>
                <Typography variant="body1" gutterBottom>
                  Please input justification for not disclosing all CPF inflow.
                </Typography>

                <TextareaAutosize
                  id="noCpfInFlowReason"
                  placeholder="Type here..."
                  style={{ width: "100%" }}
                  minRows={5}
                  onChange={handleChange}
                  defaultValue={noCpfInFlowReason}
                />

                {noCpfInFlowReasonError && (
                  <FormHelperText error={noCpfInFlowReasonError}>{noCpfInFlowReasonError}</FormHelperText>
                )}
              </>
            ) : (
              ""
            )}
          </Grid>

          {/*==============================
          CPF - OUTFLOW
          ==============================*/}
          <Grid size={12}>
            <Typography variant="h6" gutterBottom sx={{ backgroundColor: "lightgray" }}>
              CPF Outflow <b>Total: ${cpfOutlowTotal}</b>
            </Typography>
          </Grid>

          {/* CPF OUT - Ordinary Account Mortgage */}
          <GridTextfield
            id="ordinaryAcctMortgage"
            key="ordinaryAcctMortgage"
            required={false}
            columnSize="6"
            label="OA Mortgage"
            currState={ordinaryAcctMortgage}
            onChange={handleChange}
            error={ordinaryAcctMortgageError}
            helperText={ordinaryAcctMortgageError}
            isCurrency={true}
          />

          {/* CPF OUT - Ordinary Account Investments */}
          <GridTextfield
            id="ordinaryAcctInvestment"
            key="ordinaryAcctInvestment"
            required={false}
            columnSize="6"
            label="OA Regular Investments"
            currState={ordinaryAcctInvestment}
            onChange={handleChange}
            error={ordinaryAcctInvestmentError}
            helperText={ordinaryAcctInvestmentError}
            isCurrency={true}
          />

          {/* CPF OUT - Savings Account Investments */}
          <GridTextfield
            id="specialAcctInvestment"
            key="specialAcctInvestment"
            required={false}
            columnSize="6"
            label="SA Regular Investments"
            currState={specialAcctInvestment}
            onChange={handleChange}
            error={specialAcctInvestmentError}
            helperText={specialAcctInvestmentError}
            isCurrency={true}
          />

          {/* CPF OUT - Medisave Outflow */}
          <GridTextfield
            id="medisaveAcctOutflow"
            key="medisaveAcctOutflow"
            required={false}
            columnSize="6"
            label="MA Outflow"
            currState={medisaveAcctOutflow}
            onChange={handleChange}
            error={medisaveAcctOutflowError}
            helperText={medisaveAcctOutflowError}
            isCurrency={true}
          />

          {ordinaryAcctMortgage === "" &&
          ordinaryAcctInvestment === "" &&
          specialAcctInvestment === "" &&
          medisaveAcctOutflow === "" ? (
            <>
              <Grid size={12}>
                <Typography variant="body1" gutterBottom>
                  Please input justification for not disclosing all CPF outflow.
                </Typography>

                <TextareaAutosize
                  id="noCpfOutFlowReason"
                  placeholder="Type here..."
                  style={{ width: "100%" }}
                  minRows={5}
                  onChange={handleChange}
                  defaultValue={noCpfOutFlowReason}
                />
                {noCpfOutFlowReasonError && (
                  <FormHelperText error={noCpfOutFlowReasonError}>{noCpfOutFlowReasonError}</FormHelperText>
                )}
              </Grid>
            </>
          ) : (
            ""
          )}
        </Grid>

        {/* <SimplifiedInflowAndOutflowCPF /> */}
        <Grid size={12}>
          <Typography variant="body1">
            Are there any factors within the next 12 months which may significantly increase / decrease your current
            income and expenditure position?
          </Typography>

          <RadioGroup
            row
            id="haveInExImpact"
            name="haveInExImpact"
            defaultValue=""
            value={haveInExImpact}
            onChange={handleChange}
          >
            <FormControl>
              <Stack direction="row">
                <FormControlLabel id="impactYes" value="YES" control={<Radio />} label="Yes" />

                <FormControlLabel id="impactNo" value="NO" control={<Radio />} label="No" />
              </Stack>

              {haveInExImpactError ? (
                <FormHelperText error={haveInExImpactError}>{haveInExImpactError}</FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </RadioGroup>

          {haveInExImpact === "YES" && (
            <>
              <TextareaAutosize
                id="cpfInExFlowImpactDesc"
                placeholder="Type here..."
                style={{ width: "100%" }}
                minRows={5}
                onChange={handleChange}
                defaultValue={cpfInExFlowImpactDesc}
              />

              {cpfInExFlowImpactDescError && (
                <FormHelperText error={cpfInExFlowImpactDescError}>{cpfInExFlowImpactDescError}</FormHelperText>
              )}
            </>
          )}
        </Grid>

        {/* DECLARATION: DO NOT WISH TO DISCLOSE CASH FLOW INFORMATION
      <Typography
        variant="body1"
        gutterBottom
        sx={{ p: 2, my: 1, border: "1px solid", borderRadius: 5 }}
      >
        <GridCheckbox
          columnSize="12"
          label="I do not wish to diclose my cash flow information and for it to be taken into consideration for needs analysis and recommendations."
        />
      </Typography> */}
        <PageNavigator validator={validateForm} isValid={isValid} />
      </Box>
    </>
  );
}
